const STARS = [1, 2, 3, 4, 5] as const;

function Star({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 20 20" className="size-5" aria-hidden focusable="false">
      <path
        d="M10 1.6l2.5 5.2 5.7.8-4.1 4 1 5.7L10 14.6 4.9 17.3l1-5.7-4.1-4 5.7-.8L10 1.6z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Read-only aggregate: "★★★★☆ 4.2 (7)". */
export function RatingSummary({
  avg,
  count,
  className = '',
}: {
  avg: number | null;
  count: number;
  className?: string;
}) {
  if (avg === null || count === 0) {
    return <span className={`text-xs text-muted ${className}`}>Not rated yet</span>;
  }
  return (
    <span
      className={`inline-flex items-center gap-1 text-orange-ink ${className}`}
      title={`${avg.toFixed(1)} out of 5 from ${count} ${count === 1 ? 'rating' : 'ratings'}`}
    >
      <span className="inline-flex" aria-hidden>
        {STARS.map((n) => (
          <Star key={n} filled={avg >= n - 0.5} />
        ))}
      </span>
      <span className="text-xs font-medium">
        {avg.toFixed(1)}
        <span className="sr-only"> out of 5 from </span>
        <span aria-hidden> · </span>
        {count}
        <span className="sr-only"> {count === 1 ? 'rating' : 'ratings'}</span>
      </span>
    </span>
  );
}

/**
 * A rating control: five buttons plus "clear". `label` names whose rating it
 * is for screen readers ("Your rating", "Rating for Emma").
 */
export function RatingInput({
  value,
  onRate,
  onClear,
  busy = false,
  label = 'Your rating',
}: {
  value: number | null;
  onRate: (stars: number) => void;
  onClear: () => void;
  busy?: boolean;
  label?: string;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label={label}>
      {STARS.map((n) => (
        <button
          key={n}
          type="button"
          disabled={busy}
          aria-pressed={value === n}
          aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
          onClick={() => onRate(n)}
          className={`flex size-11 items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
            value !== null && n <= value ? 'text-orange-ink' : 'text-muted hover:text-orange-ink'
          }`}
        >
          <Star filled={value !== null && n <= value} />
        </button>
      ))}
      {/* Kept in the layout (invisible, so neither focusable nor announced) so rows line up. */}
      <button
        type="button"
        disabled={busy || value === null}
        onClick={onClear}
        aria-label={`Clear ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
        className={`ml-1 min-h-11 rounded-lg px-2 text-sm text-muted underline-offset-2 hover:underline disabled:opacity-50 ${value === null ? 'invisible' : ''}`}
      >
        Clear
      </button>
    </div>
  );
}

/** One person's stars, read-only: "★★★★☆" plus "4 stars" for screen readers; "Not rated" when null. */
export function MemberStars({
  stars,
  className = '',
}: {
  stars: number | null;
  className?: string;
}) {
  if (stars === null) {
    return <span className={`text-xs text-muted ${className}`}>Not rated</span>;
  }
  return (
    <span className={`inline-flex items-center text-orange-ink ${className}`}>
      <span className="inline-flex" aria-hidden>
        {STARS.map((n) => (
          <Star key={n} filled={n <= stars} />
        ))}
      </span>
      <span className="sr-only">
        {stars} {stars === 1 ? 'star' : 'stars'}
      </span>
    </span>
  );
}
